import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Receipt, 
  Download, 
  MapPin, 
  Clock, 
  Car, 
  DollarSign, 
  Star,
  Loader2,
  CheckCircle
} from 'lucide-react';

export default function TripReceipt({ rideId, onClose }) {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (rideId) {
      fetchReceipt();
    }
  }, [rideId]);

  const fetchReceipt = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/receipts/${rideId}`);
      setReceipt(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      setDownloading(true);
      const response = await api.get(`/receipts/${rideId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reddi_receipt_${receipt?.receipt_id || rideId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download receipt PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="py-8 text-center">
          <p className="text-red-600">{error}</p>
          <Button onClick={onClose} variant="outline" className="mt-4">
            Close
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!receipt) return null;

  const { fare_breakdown, trip, driver, rider } = receipt;

  return (
    <Card className="w-full max-w-md mx-auto" data-testid="trip-receipt">
      {/* Header */}
      <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Receipt className="w-8 h-8" />
            <div>
              <CardTitle className="text-xl">Trip Receipt</CardTitle>
              <p className="text-red-100 text-sm">{receipt.receipt_id}</p>
            </div>
          </div>
          <Badge className="bg-white/20 text-white border-0">
            <CheckCircle className="w-3 h-3 mr-1" />
            Paid
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* Date */}
        <div className="text-center pb-4 border-b">
          <p className="text-sm text-slate-500">
            {new Date(receipt.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>

        {/* Trip Details */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="mt-1">
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Pickup</p>
              <p className="text-sm font-medium text-slate-900">{trip.pickup}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-1">
              <div className="w-3 h-3 rounded-full bg-red-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Dropoff</p>
              <p className="text-sm font-medium text-slate-900">{trip.dropoff}</p>
            </div>
          </div>
        </div>

        {/* Trip Stats */}
        <div className="grid grid-cols-2 gap-4 py-4 border-y">
          <div className="text-center">
            <MapPin className="w-5 h-5 mx-auto text-slate-400 mb-1" />
            <p className="text-lg font-semibold">{trip.distance_miles} mi</p>
            <p className="text-xs text-slate-500">Distance</p>
          </div>
          <div className="text-center">
            <Clock className="w-5 h-5 mx-auto text-slate-400 mb-1" />
            <p className="text-lg font-semibold">{trip.duration_minutes} min</p>
            <p className="text-xs text-slate-500">Duration</p>
          </div>
        </div>

        {/* Driver Info */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
          <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
            <Car className="w-6 h-6 text-slate-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">{driver.name}</p>
            <p className="text-sm text-slate-500">{driver.vehicle}</p>
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            <span className="font-medium">{driver.rating?.toFixed(1)}</span>
          </div>
        </div>

        {/* Fare Breakdown */}
        <div className="space-y-2">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Fare Breakdown
          </h4>
          
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Base Fare</span>
              <span>${fare_breakdown.base_fare.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Mileage Charge</span>
              <span>${fare_breakdown.mileage_charge.toFixed(2)}</span>
            </div>
            {fare_breakdown.surge_fee > 0 && (
              <div className="flex justify-between text-amber-600">
                <span>Surge ({fare_breakdown.surge_multiplier}x)</span>
                <span>${fare_breakdown.surge_fee.toFixed(2)}</span>
              </div>
            )}
            {fare_breakdown.promo_discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Promo Discount</span>
                <span>-${fare_breakdown.promo_discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t">
              <span className="text-slate-600">Subtotal</span>
              <span>${fare_breakdown.subtotal.toFixed(2)}</span>
            </div>
            {fare_breakdown.tip > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-600">Tip</span>
                <span>${fare_breakdown.tip.toFixed(2)}</span>
              </div>
            )}
          </div>
          
          <div className="flex justify-between pt-3 border-t text-lg font-bold">
            <span>Total</span>
            <span className="text-red-600">${fare_breakdown.total.toFixed(2)}</span>
          </div>
        </div>

        {/* Rating */}
        {receipt.rating && (
          <div className="text-center py-3 bg-amber-50 rounded-lg">
            <p className="text-sm text-slate-600 mb-1">Your Rating</p>
            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-5 h-5 ${
                    star <= receipt.rating
                      ? 'text-amber-500 fill-amber-500'
                      : 'text-slate-300'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            className="flex-1 bg-red-600 hover:bg-red-700"
            onClick={downloadPDF}
            disabled={downloading}
            data-testid="download-receipt-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
